import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Grid, AlertColor } from '@mui/material';
import Form from 'components/Form';
import { BookingLogsForm } from 'screens/Authenticated/BookingDetail/BookingDetailTabs/BookingLogsForm';
import strings from 'common/Translation/Translate';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import dayjs from 'dayjs';
import Notification from 'components/Notification';
import DrawerMenu from 'components/DrawerMenu';

interface SalesCallFollowUpFormProps {
  open: boolean;
  onClose: () => void;
  salesCallId: number;
  assignees: any[];
  followUpModes: any[];
  onSuccess: () => void;
}

const SalesCallFollowUpForm: React.FC<SalesCallFollowUpFormProps> = ({
  open,
  onClose,
  salesCallId,
  assignees,
  followUpModes,
  onSuccess,
}) => {
  const formRef = useRef<any>();
  /** Sync guard so a second submit click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });
  const [loading, setLoading] = useState(false);

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open && formRef.current?.resetForm) {
      formRef.current.resetForm();
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current || loading) return;
    if (!formRef.current) return;
    const { getFormData } = formRef.current;
    const { formData, isFormValid } = getFormData();
    setHasError(false);
    if (isFormValid) {
      savingRef.current = true;
      try {
        setLoading(true);
        const data = formData;
        const formPayload = new FormData();
        formPayload.append('followUpDate', dayjs(data.followUpDate).format('YYYY-MM-DD'));
        formPayload.append('followUpMode', data.followUpMode);
        if (data.assignedTo) {
          formPayload.append('assignedTo', data.assignedTo);
        }
        formPayload.append('description', data.remarks);
        if (data.attachment?.length) {
          data.attachment.forEach((file: any) => {
            formPayload.append('file', file);
          });
        } else {
          formPayload.append('file', null as any);
        }
        const config = { headers: { 'Content-Type': 'multipart/form-data' } };
        const { status } = await axiosInstance.post(URLS.addSalesCallFollowUps(salesCallId), formPayload, config);
        if (status === 200) {
          setMessage({ display: true, severity: 'success', message: 'Follow-up added successfully' });
          // Reset form after successful submission
          if (formRef.current?.resetForm) {
            formRef.current.resetForm();
          }
          onSuccess();
          onClose();
        }
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response?.data?.message || 'Failed to add follow-up',
        });
      } finally {
        savingRef.current = false;
        setLoading(false);
      }
    } else {
      setHasError(true);
    }
  };

  return (
    <DrawerMenu width={'60vw'} open={open} handleClose={onClose} closeButton title="Add Follow-up">
      <Box sx={{ p: 2 }}>
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
            autoHideDuration={2000}
          />
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Form
              hasError={hasError}
              ref={formRef}
              model={BookingLogsForm(strings, assignees, followUpModes)}
              values={{}}
            />
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={onClose} sx={{ mr: 2 }} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={loading}>
              Add Follow-up
            </Button>
          </Box>
        </form>
      </Box>
    </DrawerMenu>
  );
};

export default SalesCallFollowUpForm;
